/** Cliente HTTP del módulo de Mantenimiento / OT (Fase 2). Espejo de app/schemas/mantenimiento.py. */
import { apiDownload, apiPostArchivo, apiRequest } from "../../lib/api";
import type { FilaImportacion, ResultadoImportacion } from "../../components/ImportarPlanillaModal";

// ── Tipo de activo ──
export interface TipoActivoRead {
  id: number;
  nombre: string;
  descripcion: string | null;
  plantilla_datos: Record<string, unknown> | null;
}
export interface TipoActivoCreate {
  nombre: string;
  descripcion?: string | null;
}
export type TipoActivoUpdate = Partial<TipoActivoCreate>;

// ── Activo ──
export type EstadoActivo = "Operativo" | "EnMantencion" | "Falla" | "Baja";
/** Estado físico levantado en terreno (distinto del estado operativo). */
export type Conservacion = "Bueno" | "Regular" | "Malo";

export interface ActivoRead {
  id: number;
  tipo_id: number;
  nombre: string;
  codigo_interno: string | null;
  marca: string | null;
  modelo: string | null;
  numero_serie: string | null;
  ubicacion: string | null;
  latitud: number | null;
  longitud: number | null;
  estado: EstadoActivo;
  fecha_instalacion: string | null;
  fecha_baja: string | null;
  datos_tecnicos: Record<string, unknown> | null;
  medidor_id: number | null;
  estado_conservacion: Conservacion | null;
  responsable: string | null;
  fecha_adquisicion: string | null;
  valor_adquisicion: number | null;
  proveedor_id: number | null;
  documento_compra: string | null;
  foto_storage_key: string | null;
  observacion: string | null;
  /** Con etiqueta impresa el código ya no se cambia. */
  etiquetado_at: string | null;
}
export interface ActivoCreate {
  tipo_id: number;
  nombre: string;
  codigo_interno?: string | null;
  marca?: string | null;
  modelo?: string | null;
  numero_serie?: string | null;
  ubicacion?: string | null;
  fecha_instalacion?: string | null;
  estado_conservacion?: Conservacion | null;
  responsable?: string | null;
  fecha_adquisicion?: string | null;
  valor_adquisicion?: number | null;
  documento_compra?: string | null;
  observacion?: string | null;
}
export type ActivoUpdate = Partial<ActivoCreate>;

export interface ImportacionActivos extends ResultadoImportacion {
  tipos_creados: string[];
  errores: FilaImportacion[];
}

// ── Órdenes de trabajo ──
export type TipoOT =
  | "Preventivo"
  | "Correctivo"
  | "Emergencia"
  | "Calibracion"
  | "Instalacion";
export type PrioridadOT = "Baja" | "Media" | "Alta" | "Critica";
export type EstadoOT =
  | "Pendiente"
  | "Asignada"
  | "EnProceso"
  | "EsperaRepuestos"
  | "Finalizada"
  | "Cancelada";

/** Etiquetas de pantalla: los códigos del backend no se muestran crudos. */
export const ESTADO_OT_LABELS: Record<EstadoOT, string> = {
  Pendiente: "Pendiente",
  Asignada: "Asignada",
  EnProceso: "En proceso",
  EsperaRepuestos: "Esperando repuestos",
  Finalizada: "Finalizada",
  Cancelada: "Cancelada",
};

export const TIPO_OT_LABELS: Record<TipoOT, string> = {
  Preventivo: "Preventivo",
  Correctivo: "Correctivo",
  Emergencia: "Emergencia",
  Calibracion: "Calibración",
  Instalacion: "Instalación",
};

export const PRIORIDAD_OT_LABELS: Record<PrioridadOT, string> = {
  Baja: "Baja",
  Media: "Media",
  Alta: "Alta",
  Critica: "Crítica",
};

/** `2026-0001`: correlativo anual de la OT. */
export function numeroOT(o: { anio: number; numero: number }): string {
  return `${o.anio}-${String(o.numero).padStart(4, "0")}`;
}

export interface ConsumoMaterialRead {
  id: number;
  insumo_id: number;
  bodega_id: number;
  movimiento_id: number | null;
  cantidad: number | string;
  costo_unitario: number;
  costo_total: number;
}

export interface OrdenTrabajoRead {
  id: number;
  anio: number;
  numero: number;
  plan_id: number | null;
  activo_id: number | null;
  atencion_id: number | null;
  orden_corte_id: number | null;
  medidor_id: number | null;
  tipo: TipoOT;
  prioridad: PrioridadOT;
  estado: EstadoOT;
  fecha_programada: string | null;
  fecha_inicio_real: string | null;
  fecha_fin_real: string | null;
  fecha_vencimiento: string | null;
  descripcion_solicitud: string;
  informe_tecnico: string | null;
  motivo_cancelacion: string | null;
  solicitado_por_id: number | null;
  asignado_a_id: number | null;
  costo_mano_obra: number;
  costo_materiales: number;
  costo_servicios: number;
  costo_total: number;
  consumos: ConsumoMaterialRead[];
}

export interface OrdenTrabajoCreate {
  tipo: TipoOT;
  descripcion_solicitud: string;
  prioridad?: PrioridadOT;
  activo_id?: number | null;
  /** Medidor del padrón a revisar (derivación desde /consumos). */
  medidor_id?: number | null;
  /** Atención de origen (derivación desde /atenciones). */
  atencion_id?: number | null;
  fecha_programada?: string | null;
  fecha_vencimiento?: string | null;
  asignado_a_id?: number | null;
}

export interface HojaVidaActivo {
  activo: ActivoRead;
  ordenes: OrdenTrabajoRead[];
}

// ── Planes de mantención preventiva ──
export interface PlanMantenimientoRead {
  id: number;
  nombre: string;
  descripcion: string | null;
  activo_id: number | null;
  tipo_activo_id: number | null;
  frecuencia_dias: number;
  alerta_dias_antes: number;
  protocolo: string | null;
  requiere_parada_planta: boolean;
  tiempo_estimado_horas: number | string | null;
  activo: boolean;
}
export interface PlanMantenimientoCreate {
  nombre: string;
  descripcion?: string | null;
  activo_id?: number | null;
  tipo_activo_id?: number | null;
  frecuencia_dias: number;
  alerta_dias_antes?: number;
  protocolo?: string | null;
  requiere_parada_planta?: boolean;
}
export type PlanMantenimientoUpdate = Partial<PlanMantenimientoCreate> & { activo?: boolean };

const B = "/api/v1/mantenimiento";

// Tipos de activo
export const listarTiposActivo = (): Promise<TipoActivoRead[]> =>
  apiRequest(`${B}/tipos-activo`);
export const crearTipoActivo = (p: TipoActivoCreate): Promise<TipoActivoRead> =>
  apiRequest(`${B}/tipos-activo`, { method: "POST", body: p });
export const actualizarTipoActivo = (
  id: number,
  p: TipoActivoUpdate,
): Promise<TipoActivoRead> =>
  apiRequest(`${B}/tipos-activo/${id}`, { method: "PATCH", body: p });

// Activos
export const listarActivos = (): Promise<ActivoRead[]> => apiRequest(`${B}/activos`);
export const crearActivo = (p: ActivoCreate): Promise<ActivoRead> =>
  apiRequest(`${B}/activos`, { method: "POST", body: p });
export const actualizarActivo = (id: number, p: ActivoUpdate): Promise<ActivoRead> =>
  apiRequest(`${B}/activos/${id}`, { method: "PATCH", body: p });
export const cambiarEstadoActivo = (
  id: number,
  estado: EstadoActivo,
): Promise<ActivoRead> =>
  apiRequest(`${B}/activos/${id}/estado`, { method: "POST", body: { estado } });
export const hojaVidaActivo = (id: number): Promise<HojaVidaActivo> =>
  apiRequest(`${B}/activos/${id}/hoja-vida`);
export const importarActivos = (archivo: File, confirmar: boolean): Promise<ImportacionActivos> =>
  apiPostArchivo(`${B}/activos/importar?confirmar=${confirmar}`, archivo);
export const descargarPlantillaActivos = (): Promise<void> =>
  apiDownload(`${B}/activos/plantilla.xlsx`, "planilla_activos.xlsx");
export const exportarActivos = (): Promise<void> =>
  apiDownload(`${B}/activos/export.xlsx`, "registro_activos.xlsx");

// Órdenes de trabajo
export const listarOrdenes = (params?: {
  estado?: EstadoOT;
  tipo?: TipoOT;
  activo_id?: number;
}): Promise<OrdenTrabajoRead[]> => {
  const q = new URLSearchParams();
  if (params?.estado) q.set("estado", params.estado);
  if (params?.tipo) q.set("tipo", params.tipo);
  if (params?.activo_id != null) q.set("activo_id", String(params.activo_id));
  const qs = q.toString();
  return apiRequest(`${B}/ordenes-trabajo${qs ? `?${qs}` : ""}`);
};
export const crearOrden = (p: OrdenTrabajoCreate): Promise<OrdenTrabajoRead> =>
  apiRequest(`${B}/ordenes-trabajo`, { method: "POST", body: p });
export const obtenerOrden = (id: number): Promise<OrdenTrabajoRead> =>
  apiRequest(`${B}/ordenes-trabajo/${id}`);
export const asignarOrden = (id: number, asignado_a_id: number): Promise<OrdenTrabajoRead> =>
  apiRequest(`${B}/ordenes-trabajo/${id}/asignar`, {
    method: "POST",
    body: { asignado_a_id },
  });
export const iniciarOrden = (id: number): Promise<OrdenTrabajoRead> =>
  apiRequest(`${B}/ordenes-trabajo/${id}/iniciar`, { method: "POST" });
export const esperaRepuestosOrden = (id: number): Promise<OrdenTrabajoRead> =>
  apiRequest(`${B}/ordenes-trabajo/${id}/espera-repuestos`, { method: "POST" });
export const finalizarOrden = (
  id: number,
  body: {
    informe_tecnico: string;
    costo_mano_obra?: number;
    costo_servicios?: number;
    /** Estado en que queda el activo tras el trabajo (Operativo / EnMantencion / Falla). */
    estado_activo?: Exclude<EstadoActivo, "Baja"> | null;
  },
): Promise<OrdenTrabajoRead> =>
  apiRequest(`${B}/ordenes-trabajo/${id}/finalizar`, { method: "POST", body });
/** Devuelve a bodega el material de un consumo (reversa + recalcula costos). */
export const quitarConsumo = (
  id: number,
  consumoId: number,
  motivo: string,
): Promise<OrdenTrabajoRead> =>
  apiRequest(`${B}/ordenes-trabajo/${id}/consumos/${consumoId}/quitar`, {
    method: "POST",
    body: { motivo },
  });
export const cancelarOrden = (id: number, motivo: string): Promise<OrdenTrabajoRead> =>
  apiRequest(`${B}/ordenes-trabajo/${id}/cancelar`, { method: "POST", body: { motivo } });
export const agregarConsumo = (
  id: number,
  body: { insumo_id: number; bodega_id: number; cantidad: number },
): Promise<ConsumoMaterialRead> =>
  apiRequest(`${B}/ordenes-trabajo/${id}/consumos`, { method: "POST", body });

// Planes
export const listarPlanes = (): Promise<PlanMantenimientoRead[]> => apiRequest(`${B}/planes`);
export const crearPlan = (p: PlanMantenimientoCreate): Promise<PlanMantenimientoRead> =>
  apiRequest(`${B}/planes`, { method: "POST", body: p });
export const actualizarPlan = (
  id: number,
  p: PlanMantenimientoUpdate,
): Promise<PlanMantenimientoRead> =>
  apiRequest(`${B}/planes/${id}`, { method: "PATCH", body: p });
export const generarPreventivas = (): Promise<{ creadas: number }> =>
  apiRequest(`${B}/planes/generar-preventivas`, { method: "POST" });
